import { OcdErrorCodesEnum } from '@/api/models/OcdErrorCodes.enum.ts';
import { HTTP_STATUS } from '@/api/util/ErrorCode.model.ts';
import { isAxiosError } from '@/api/util/isAxiosError.guard.ts';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';
import { LOGIN_REDIRECT_URL, PATHS_NOT_REQUIRING_AUTHORIZATION } from '@/model/routes.consts.ts';
import { SYSTEM_ROUTS } from '@/routes/SystemRoutes.enum.ts';
import { LocalStorageParam, setLocalStorage } from '@/utils/localStorage.util.ts';

interface Options {
  /**
   * Toast sink for business 403 responses that should only inform: the query
   * cache passes a translated ErrorBoundary handler, loaders have none and
   * treat those errors as unhandled instead.
   */
  onMinorError?: (forbiddenStatus: number) => void;
}

const MAINTENANCE_PATH: string = RouteEnum.MAINTENANCE;

/**
 * Shared window-location handling for auth failures (OCD-309): used by the
 * query cache error handler and by route guard loaders so both entry paths
 * redirect identically on maintenance, 401 and 403 responses. Returns true
 * when the error was handled (redirect or informed minor error).
 */
export const redirectOnAuthFailure = (error: unknown, options: Options = {}): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }

  if (error.response?.status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
    const data = error.response.data as {
      maintenanceEndDate?: string;
      status?: OcdErrorCodesEnum;
    };
    if (data?.status === OcdErrorCodesEnum.MAINTENANCE) {
      if (window.location.pathname === MAINTENANCE_PATH) {
        return true;
      }
      const params = data.maintenanceEndDate
        ? `?endDate=${encodeURIComponent(data.maintenanceEndDate)}`
        : '';
      (window as Window).location = window.location.origin + MAINTENANCE_PATH + params;
      return true;
    }
  }

  if (
    error.response?.status !== HTTP_STATUS.FORBIDDEN &&
    error.response?.status !== HTTP_STATUS.UNAUTHORIZED
  ) {
    return false;
  }

  const pathname = window.location.pathname;
  if (PATHS_NOT_REQUIRING_AUTHORIZATION.some(path => pathname.includes(path))) {
    return true;
  }

  if (error.response.status === HTTP_STATUS.FORBIDDEN) {
    const forbiddenStatus = (error.response.data as { status?: number })?.status;
    if (forbiddenStatus) {
      options.onMinorError?.(forbiddenStatus);
      return true;
    }
    (window as Window).location = window.location.origin + RouteEnum.NOT_AUTHORIZED;
    return true;
  }

  if (error.response.data && (error.response.data as { status?: number }).status) {
    const { status } = error.response.data as { status?: OcdErrorCodesEnum };

    if (status === OcdErrorCodesEnum.NOT_AUTHORIZED) {
      (window as Window).location = window.location.origin + RouteEnum.NOT_AUTHORIZED;
      return true;
    }
    if (status === OcdErrorCodesEnum.WAIT_FOR_APPROVAL) {
      (window as Window).location = window.location.origin + RouteEnum.WAITING_FOR_APPROVAL;
      return true;
    }
  }

  if (!SYSTEM_ROUTS.includes(pathname)) {
    setLocalStorage(LocalStorageParam.LAST_VISITED_LOCATION, pathname);
  }

  window.location.replace(LOGIN_REDIRECT_URL);
  return true;
};
